// deps
import React, { memo, useCallback, useState } from "react";

// internal-deps
import { MenuItemsCalloutInfo } from "@swiggy-private/cart-types";
import { MENU_ITEMS_CALLOUT_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

// components
import HalfCardWidget from "../half-card-widget";
import { TriggerTextContainer } from "./HalfCardTrigger.style";

// helpers
import { shouldShowHalfCardTrigger } from "@cart-ui/helpers/MenuItemsCallout";

// constants
import { HALF_CARD_ACTION } from "../half-card-widget/constants";

interface HalfCardTriggerProps {
    calloutInfo?: MenuItemsCalloutInfo;
    onHalfCardTriggerClick?: () => void;
    onHalfCardViewImpression?: () => void;
    onHalfCardCtaClick?: () => void;
    triggerCTAColor?: keyof IColors;
    hideTriggerCTABorder?: boolean;
    dataTestId?: string;
    isGiftingCart?: boolean;
}

function HalfCardTrigger({
    calloutInfo,
    onHalfCardTriggerClick,
    onHalfCardViewImpression,
    onHalfCardCtaClick,
    triggerCTAColor = "text_Med_Emphasis",
    hideTriggerCTABorder = false,
    dataTestId = MENU_ITEMS_CALLOUT_TEST_IDS.MENU_ITEMS_CALLOUT_HALF_CARD_TRIGGER,
    isGiftingCart = false,
}: HalfCardTriggerProps) {
    const [showHalfCard, setShowHalfCard] = useState(false);

    const handleShowHalfCard = useCallback(() => {
        setShowHalfCard(true);
        onHalfCardTriggerClick?.();
        onHalfCardViewImpression?.();
    }, [onHalfCardTriggerClick, onHalfCardViewImpression]);

    const handleHideHalfCard = useCallback(
        (action: string) => {
            if (action === HALF_CARD_ACTION.CTA) {
                onHalfCardCtaClick?.();
            }

            setShowHalfCard(false);
        },
        [onHalfCardCtaClick],
    );

    if (!shouldShowHalfCardTrigger(calloutInfo)) {
        return null;
    }

    return (
        <>
            <TriggerTextContainer
                variant="V2.body3.neutral.bold"
                color={triggerCTAColor}
                onClick={handleShowHalfCard}
                data-testid={dataTestId}
                hideBorder={hideTriggerCTABorder}
            >
                {calloutInfo?.half_card_trigger_text}
            </TriggerTextContainer>

            <HalfCardWidget
                showHalfCard={showHalfCard}
                halfCardInfo={calloutInfo?.half_card_info}
                onClose={handleHideHalfCard}
                isGiftingCart={isGiftingCart}
            />
        </>
    );
}

export default memo(HalfCardTrigger);
